import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import dayjs from 'common/dayjs';
import './TimeCalendar.scss';
import PrimaryButton from 'util/PrimaryButton';
import { TimeCalendarProps } from 'types/TimeCalendarProps';
import { convertToIsoFormatFromDayAndHour } from 'util/DateTimeUtil';
import { useTenpo } from 'context/TenpoContext';
import { ReservationMode } from 'types/ReservationMode';

interface TableCell {
  day: string;
  hour: string;
}

export const TimeCalendar: React.FC<TimeCalendarProps> = (
  props: TimeCalendarProps,
): JSX.Element => {
  const { handleClickCell, getReservationScheduleList, reservationSchedule } = props;
  const [selectedCell, setSelectedCell] = useState<TableCell | null>(null);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const tenpo = useTenpo();

  const { timetables = [], schedule = {} } = reservationSchedule || {};

  const MAX_DATE_LENGTH = 14; //2週間先まで予約可能
  const range = 7;
  const dates = Object.keys(schedule);
  const week = dates.slice(currentDateIndex, currentDateIndex + range);

  const hours: string[] = timetables.map((t): string => {
    return `${t.hour}:${dayjs().minute(t.minutes).format('mm')}`;
  });

  useEffect(() => {
    setSelectedCell(null);
  }, [reservationSchedule]);

  const handleClickSelector = (day: string, hour: string, reservationStatusCode: number) => {
    setSelectedCell({ day, hour });
    const isoFormat = convertToIsoFormatFromDayAndHour(day, hour);
    handleClickCell(isoFormat, reservationStatusCode);
  };

  const handleNextWeek = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (dates.length < MAX_DATE_LENGTH) {
      await getReservationScheduleList(1);
    }
    setCurrentDateIndex(currentDateIndex + range);
  };

  const handlePreviousWeek = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setCurrentDateIndex(currentDateIndex - range);
  };

  return (
    <div className="time-calendar">
      <div className="d-flex justify-content-between align-items-center">
        <PrimaryButton
          label="前の１週間"
          onClick={handlePreviousWeek}
          type="button"
          disabled={currentDateIndex === 0}
        />
        <PrimaryButton
          label="次の１週間"
          onClick={handleNextWeek}
          type="button"
          disabled={currentDateIndex + range >= MAX_DATE_LENGTH}
        />
      </div>
      <Table className="table table-borderless table-sm">
        <thead>
          <tr>
            <th></th>
            {week.map((day, index) => (
              <th key={index}>
                {dayjs(day).format('M/D')}
                <br />
                {dayjs(day).format('(ddd)')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hours.map((hour, rowIndex) => (
            <tr key={rowIndex}>
              <th className="hour">{hour}</th>
              {week.map((day, colIndex) => (
                <td
                  key={colIndex}
                  className={` ${
                    selectedCell?.day === day && selectedCell?.hour === hour ? 'selected' : ''
                  }`}
                >
                  <div
                    onClick={
                      schedule[day][rowIndex] <= 0 ||
                      schedule[day][rowIndex] === ReservationMode.TEL
                        ? undefined
                        : () => handleClickSelector(day, hour, schedule[day][rowIndex])
                    }
                    className={`time-calendar__selector ${
                      schedule[day][rowIndex] <= ReservationMode.NOT_ACCEPT
                        ? 'unavailable'
                        : schedule[day][rowIndex] == ReservationMode.ACCEPT
                          ? 'available'
                          : schedule[day][rowIndex] == ReservationMode.REQUEST
                            ? 'pending'
                            : schedule[day][rowIndex] === ReservationMode.TEL
                              ? 'tel'
                              : undefined
                    }`}
                  >
                    {schedule[day][rowIndex] === 3 && (
                      <a href={`tel:${tenpo.tenpoPhone}`}>{'TEL'}</a>
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default TimeCalendar;
